"use strict";
import { UniverseChainId, RPCType } from "features/chains/types";
import { getBscSubgraphs } from "constants/bscContracts";
import { fetchFromSubgraph } from "utils/fetchSubgraph";
import { getContractAbi, getContractAddress } from "constants/contracts";
import { getChainInfo } from "features/chains/chainInfo";
import { createPublicClient, http } from "viem";
import { bsc, bscTestnet } from "viem/chains";
import {
  fetchV2MultiHopPoolsOnChain,
  fetchV2PoolsOnChain,
  fetchV3MultiHopPoolsOnChain,
  shouldUseOnChainV2
} from "./bscOnChainPools";
const V2_POOLS_QUERY = `
  query GetV2Pools($tokenIn: String!, $tokenOut: String!) {
    pairs(
      where: {
        or: [
          { token0: $tokenIn, token1: $tokenOut }
          { token0: $tokenOut, token1: $tokenIn }
        ]
      }
      orderBy: reserveUSD
      orderDirection: desc
    ) {
      id
      token0 {
        id
        symbol
        decimals
        name
      }
      token1 {
        id
        symbol
        decimals
        name
      }
      reserve0
      reserve1
      reserveUSD
      stable
    }
  }
`;
const V2_MULTI_HOP_QUERY = `
  query GetV2MultiHopPools($tokens: [String!]!) {
    pairs(
      where: {
        and: [
          {
            or: [
              { token0_in: $tokens }
              { token1_in: $tokens }
            ]
          }
          { reserveUSD_gt: "100" }
        ]
      }
      first: 50
      orderBy: reserveUSD
      orderDirection: desc
    ) {
      id
      token0 {
        id
        symbol
        decimals
        name
      }
      token1 {
        id
        symbol
        decimals
        name
      }
      reserve0
      reserve1
      reserveUSD
      stable
    }
  }
`;
const V3_POOLS_QUERY = `
  query GetV3Pools($tokenIn: String!, $tokenOut: String!) {
    pools(
      where: {
        or: [
          { token0: $tokenIn, token1: $tokenOut }
          { token0: $tokenOut, token1: $tokenIn }
        ]
      }
      orderBy: totalValueLockedUSD
      orderDirection: desc
    ) {
      id
      token0 {
        id
        symbol
        decimals
        name
      }
      token1 {
        id
        symbol
        decimals
        name
      }
      tickSpacing
      liquidity
      sqrtPrice
      tick
      totalValueLockedUSD
      totalValueLockedToken0
      totalValueLockedToken1
    }
  }
`;
const V2_TOP_POOLS_QUERY = `
  query GetTopV2Pools($first: Int!) {
    pairs(
      first: $first
      orderBy: reserveUSD
      orderDirection: desc
      where: { reserveUSD_gt: "1000" }
    ) {
      id
      token0 {
        id
        symbol
        decimals
        name
      }
      token1 {
        id
        symbol
        decimals
        name
      }
      reserve0
      reserve1
      reserveUSD
      stable
    }
  }
`;
const V3_TOP_POOLS_QUERY = `
  query GetTopV3Pools($first: Int!) {
    pools(
      first: $first
      orderBy: totalValueLockedUSD
      orderDirection: desc
      where: { totalValueLockedUSD_gt: "1000" }
    ) {
      id
      token0 {
        id
        symbol
        decimals
        name
      }
      token1 {
        id
        symbol
        decimals
        name
      }
      tickSpacing
      liquidity
      sqrtPrice
      tick
      totalValueLockedUSD
      totalValueLockedToken0
      totalValueLockedToken1
    }
  }
`;
const gaugeCacheByChain = /* @__PURE__ */ new Map();
const gaugeCacheTimestampByChain = /* @__PURE__ */ new Map();
const GAUGE_CACHE_TTL = 5 * 60 * 1e3;
async function fetchGaugesOnChain(chainId) {
  const now = Date.now();
  const cachedGauges = gaugeCacheByChain.get(chainId);
  const cachedTimestamp = gaugeCacheTimestampByChain.get(chainId) ?? 0;
  if (cachedGauges && now - cachedTimestamp < GAUGE_CACHE_TTL) {
    return cachedGauges;
  }
  const voterAddress = getContractAddress(chainId, "Voter");
  const voterAbi = getContractAbi(chainId, "Voter");
  if (!voterAddress || !voterAbi) {
    return /* @__PURE__ */ new Map();
  }
  try {
    const chainInfo = getChainInfo(chainId);
    const rpcUrl = chainInfo.rpcUrls?.[RPCType.Default]?.http?.[0] ?? chainInfo.rpcUrls?.[RPCType.Public]?.http?.[0];
    if (!rpcUrl) {
      console.warn("[fetchGaugesOnChain] No RPC URL configured for chain", chainId);
      return /* @__PURE__ */ new Map();
    }
    const client = createPublicClient({
      chain: chainId === UniverseChainId.Sepolia ? bscTestnet : bsc,
      transport: http(rpcUrl)
    });
    const lengthResult = await client.readContract({
      address: voterAddress,
      abi: voterAbi,
      functionName: "length",
      args: []
    });
    const gaugeCount = Number(lengthResult);
    if (gaugeCount === 0) return /* @__PURE__ */ new Map();
    const poolCalls = Array.from({ length: gaugeCount }, (_, i) => ({
      address: voterAddress,
      abi: voterAbi,
      functionName: "pools",
      args: [BigInt(i)]
    }));
    const poolsResults = await client.multicall({
      contracts: poolCalls,
      allowFailure: true
    });
    const poolAddresses = poolsResults.filter((r) => r.status === "success").map((r) => r.result.toLowerCase());
    const gaugeCalls = poolAddresses.map((poolAddr) => ({
      address: voterAddress,
      abi: voterAbi,
      functionName: "gauges",
      args: [poolAddr]
    }));
    const gaugesResults = await client.multicall({
      contracts: gaugeCalls,
      allowFailure: true
    });
    const gaugesMap = /* @__PURE__ */ new Map();
    gaugesResults.forEach((result, i) => {
      if (result.status === "success") {
        const poolAddr = poolAddresses[i];
        const gaugeAddr = result.result.toLowerCase();
        gaugesMap.set(poolAddr, gaugeAddr);
      }
    });
    gaugeCacheByChain.set(chainId, gaugesMap);
    gaugeCacheTimestampByChain.set(chainId, now);
    return gaugesMap;
  } catch (error) {
    console.error("[fetchTopPoolsByTVL] Failed to fetch gauges on-chain:", error);
    return gaugeCacheByChain.get(chainId) || /* @__PURE__ */ new Map();
  }
}
const V3_POOL_TICKS_QUERY = `
  query GetPoolTicks($poolAddress: String!, $skip: Int!) {
    ticks(
      where: { poolAddress: $poolAddress }
      orderBy: tickIdx
      orderDirection: asc
      first: 1000
      skip: $skip
    ) {
      tickIdx
      liquidityGross
      liquidityNet
      price0
      price1
    }
  }
`;
const V3_POOL_PRICE_HISTORY_QUERY = `
  query PoolPriceHistory($poolId: String!, $from: Int!, $pageSize: Int!, $skip: Int!) {
    poolDayDatas(
      first: $pageSize
      skip: $skip
      where: { pool: $poolId, date_gte: $from }
      orderBy: date
      orderDirection: asc
    ) {
      date
      token0Price
      token1Price
      open
      high
      low
      close
    }
  }
`;
const V3_POOL_HOUR_HISTORY_QUERY = `
  query PoolHourHistory($poolId: String!, $from: Int!, $pageSize: Int!, $skip: Int!) {
    poolHourDatas(
      first: $pageSize
      skip: $skip
      where: { pool: $poolId, periodStartUnix_gte: $from }
      orderBy: periodStartUnix
      orderDirection: asc
    ) {
      periodStartUnix
      token0Price
      token1Price
      open
      high
      low
      close
    }
  }
`;
async function safeFetchFromSubgraph(endpoint, options, label) {
  try {
    const requestId = options.requestId ?? label;
    return await fetchFromSubgraph(endpoint, { ...options, requestId });
  } catch (error) {
    console.error(`[BscSubgraph] ${label} failed:`, error);
    return null;
  }
}
async function withOnChainFallback(context, subgraphFetch, onChainFetch) {
  try {
    const result = await subgraphFetch();
    if (result.length > 0) return result;
    console.warn(`[${context}] subgraph returned 0 results \u2014 falling back to on-chain`);
  } catch (error) {
    console.error(`[${context}] subgraph fetch failed \u2014 falling back to on-chain:`, error);
  }
  try {
    return await onChainFetch();
  } catch (fallbackError) {
    console.error(`[${context}] on-chain fallback failed:`, fallbackError);
    return [];
  }
}
export async function fetchV2Pools(tokenIn, tokenOut, chainId = UniverseChainId.Mainnet) {
  if (shouldUseOnChainV2(chainId)) {
    return fetchV2PoolsOnChain(tokenIn, tokenOut, chainId);
  }
  return withOnChainFallback(
    "fetchV2Pools",
    async () => {
      const subgraphs = getBscSubgraphs(chainId);
      const data = await fetchFromSubgraph(subgraphs.v2, {
        query: V2_POOLS_QUERY,
        variables: {
          tokenIn: tokenIn.toLowerCase(),
          tokenOut: tokenOut.toLowerCase()
        },
        requestId: "fetchV2Pools"
      });
      return data?.pairs ?? [];
    },
    () => fetchV2PoolsOnChain(tokenIn, tokenOut, chainId)
  );
}
export async function fetchV3Pools(tokenIn, tokenOut, chainId = UniverseChainId.Mainnet) {
  if (shouldUseOnChainV2(chainId)) {
    return fetchV3MultiHopPoolsOnChain([tokenIn, tokenOut], chainId);
  }
  return withOnChainFallback(
    "fetchV3Pools",
    async () => {
      const subgraphs = getBscSubgraphs(chainId);
      const data = await fetchFromSubgraph(subgraphs.v3, {
        query: V3_POOLS_QUERY,
        variables: {
          tokenIn: tokenIn.toLowerCase(),
          tokenOut: tokenOut.toLowerCase()
        },
        requestId: "fetchV3Pools"
      });
      return data?.pools ?? [];
    },
    () => fetchV3MultiHopPoolsOnChain([tokenIn, tokenOut], chainId)
  );
}
export async function fetchPoolsLazy(tokenIn, tokenOut, chainId = UniverseChainId.Mainnet, commonBases = []) {
  if (shouldUseOnChainV2(chainId)) {
    const tokenSet = [tokenIn, tokenOut, ...commonBases];
    const [v2Result, v3Result] = await Promise.allSettled([
      fetchV2MultiHopPoolsOnChain(tokenSet, chainId),
      fetchV3MultiHopPoolsOnChain(tokenSet, chainId)
    ]);
    const v2Pools = v2Result.status === "fulfilled" ? v2Result.value : [];
    const v3Pools = v3Result.status === "fulfilled" ? v3Result.value : [];
    if (v2Result.status === "rejected") {
      console.error("[Pool Lazy Load][on-chain] V2 discovery failed:", v2Result.reason);
    }
    if (v3Result.status === "rejected") {
      console.error("[Pool Lazy Load][on-chain] V3 discovery failed:", v3Result.reason);
    }
    return { v2Pools, v3Pools };
  }
  try {
    const subgraphs = getBscSubgraphs(chainId);
    const tokenInLower = tokenIn.toLowerCase();
    const tokenOutLower = tokenOut.toLowerCase();
    const relevantTokens = [tokenInLower, tokenOutLower, ...commonBases.map((t) => t.toLowerCase())].filter((token, index, self) => self.indexOf(token) === index);
    const v2Query = `
      query GetRelevantV2Pools($tokens: [String!]!) {
        pairs(
          where: {
            and: [
              {
                or: [
                  { token0_in: $tokens }
                  { token1_in: $tokens }
                ]
              }
              { reserveUSD_gt: "10" }
            ]
          }
          first: 100
          orderBy: reserveUSD
          orderDirection: desc
        ) {
          id
          token0 { id symbol decimals name }
          token1 { id symbol decimals name }
          reserve0
          reserve1
          reserveUSD
          stable
        }
      }
    `;
    const v3Query = `
      query GetRelevantV3Pools($tokens: [String!]!) {
        pools(
          where: {
            and: [
              {
                or: [
                  { token0_in: $tokens }
                  { token1_in: $tokens }
                ]
              }
              { liquidity_gt: "0" }
            ]
          }
          first: 100
          orderBy: totalValueLockedUSD
          orderDirection: desc
        ) {
          id
          token0 { id symbol decimals name }
          token1 { id symbol decimals name }
          tickSpacing
          liquidity
          sqrtPrice
          tick
          totalValueLockedUSD
          totalValueLockedToken0
          totalValueLockedToken1
        }
      }
    `;
    const v2Subgraph = subgraphs.v2;
    const v3Subgraph = subgraphs.v3;
    const [v2Data, v3Data] = await Promise.all([
      safeFetchFromSubgraph(v2Subgraph, {
        query: v2Query,
        variables: { tokens: relevantTokens },
        requestId: "fetchPoolsLazy.v2"
      }, "fetchPoolsLazy.v2"),
      safeFetchFromSubgraph(v3Subgraph, {
        query: v3Query,
        variables: { tokens: relevantTokens },
        requestId: "fetchPoolsLazy.v3"
      }, "fetchPoolsLazy.v3")
    ]);
    let v2Pools = v2Data?.pairs ?? [];
    let v3Pools = v3Data?.pools ?? [];
    const tokenSet = [tokenIn, tokenOut, ...commonBases];
    const [v2Fallback, v3Fallback] = await Promise.allSettled([
      v2Pools.length === 0 ? fetchV2MultiHopPoolsOnChain(tokenSet, chainId) : Promise.resolve(v2Pools),
      v3Pools.length === 0 ? fetchV3MultiHopPoolsOnChain(tokenSet, chainId) : Promise.resolve(v3Pools)
    ]);
    if (v2Pools.length === 0) {
      if (v2Fallback.status === "fulfilled") {
        v2Pools = v2Fallback.value;
        if (v2Pools.length > 0) {
          console.warn("[Pool Lazy Load] V2 subgraph empty \u2014 recovered via on-chain");
        }
      } else {
        console.error("[Pool Lazy Load] V2 on-chain fallback failed:", v2Fallback.reason);
      }
    }
    if (v3Pools.length === 0) {
      if (v3Fallback.status === "fulfilled") {
        v3Pools = v3Fallback.value;
        if (v3Pools.length > 0) {
          console.warn("[Pool Lazy Load] V3 subgraph empty \u2014 recovered via on-chain");
        }
      } else {
        console.error("[Pool Lazy Load] V3 on-chain fallback failed:", v3Fallback.reason);
      }
    }
    return { v2Pools, v3Pools };
  } catch (error) {
    console.error("[Pool Lazy Load] Subgraph layer threw \u2014 falling back fully to on-chain:", error);
    const tokenSet = [tokenIn, tokenOut, ...commonBases];
    const [v2Result, v3Result] = await Promise.allSettled([
      fetchV2MultiHopPoolsOnChain(tokenSet, chainId),
      fetchV3MultiHopPoolsOnChain(tokenSet, chainId)
    ]);
    return {
      v2Pools: v2Result.status === "fulfilled" ? v2Result.value : [],
      v3Pools: v3Result.status === "fulfilled" ? v3Result.value : []
    };
  }
}
const TOKEN_QUERY = `
  query GetToken($tokenAddress: String!) {
    tokens(where: { id: $tokenAddress }) {
      id
      symbol
      decimals
      name
    }
  }
`;
export async function fetchTokenData(tokenAddress, chainId = UniverseChainId.Mainnet) {
  try {
    const subgraphs = getBscSubgraphs(chainId);
    const lowerAddress = tokenAddress.toLowerCase();
    const v2Subgraph = subgraphs.v2;
    const v2Data = await safeFetchFromSubgraph(
      v2Subgraph,
      {
        query: TOKEN_QUERY,
        variables: { tokenAddress: lowerAddress }
      },
      "fetchTokenData.v2"
    );
    if (v2Data?.tokens?.[0]) {
      return v2Data.tokens[0];
    }
    const v3Subgraph = subgraphs.v3;
    const v3Data = await safeFetchFromSubgraph(
      v3Subgraph,
      {
        query: TOKEN_QUERY,
        variables: { tokenAddress: lowerAddress }
      },
      "fetchTokenData.v3"
    );
    if (v3Data?.tokens?.[0]) {
      return v3Data.tokens[0];
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch token data from BSC subgraphs:", error);
    return null;
  }
}
async function _fetchV2MultiHopPools(tokens, chainId = UniverseChainId.Mainnet) {
  if (shouldUseOnChainV2(chainId)) {
    return fetchV2MultiHopPoolsOnChain(tokens, chainId);
  }
  return withOnChainFallback(
    "fetchV2MultiHopPools",
    async () => {
      const subgraphs = getBscSubgraphs(chainId);
      const data = await fetchFromSubgraph(subgraphs.v2, {
        query: V2_MULTI_HOP_QUERY,
        variables: { tokens },
        requestId: "fetchV2MultiHopPools"
      });
      return data?.pairs ?? [];
    },
    () => fetchV2MultiHopPoolsOnChain(tokens, chainId)
  );
}
export async function fetchTopPoolsByTVL(limit = 15, chainId = UniverseChainId.Mainnet) {
  if (shouldUseOnChainV2(chainId)) {
    try {
      const { getDexTokenList } = await import("constants/topazTokenList");
      const tokenAddrs = getDexTokenList(chainId).map((t) => t.address);
      const [v2Pools, gaugesMap] = await Promise.all([
        fetchV2MultiHopPoolsOnChain(tokenAddrs, chainId),
        fetchGaugesOnChain(chainId)
      ]);
      return { v2Pools: v2Pools.slice(0, limit), v3Pools: [], gauges: gaugesMap };
    } catch (error) {
      console.error("[fetchTopPoolsByTVL] On-chain fallback failed:", error);
      return { v2Pools: [], v3Pools: [], gauges: /* @__PURE__ */ new Map() };
    }
  }
  try {
    const subgraphs = getBscSubgraphs(chainId);
    const v2Subgraph = subgraphs.v2;
    const v3Subgraph = subgraphs.v3;
    const [v2Data, v3Data, gaugesMap] = await Promise.all([
      safeFetchFromSubgraph(
        v2Subgraph,
        {
          query: V2_TOP_POOLS_QUERY,
          variables: { first: Math.ceil(limit * 0.6) },
          requestId: "fetchTopPoolsByTVL.v2"
        },
        "fetchTopPoolsByTVL.v2"
      ),
      safeFetchFromSubgraph(
        v3Subgraph,
        {
          query: V3_TOP_POOLS_QUERY,
          variables: { first: Math.ceil(limit * 0.4) },
          requestId: "fetchTopPoolsByTVL.v3"
        },
        "fetchTopPoolsByTVL.v3"
      ),
      // Fetch gauges from on-chain instead of subgraph
      fetchGaugesOnChain(chainId)
    ]);
    const v2Pools = v2Data?.pairs ?? [];
    const v3Pools = v3Data?.pools ?? [];
    return { v2Pools, v3Pools, gauges: gaugesMap };
  } catch (error) {
    console.error("Failed to fetch top pools by TVL:", error);
    return { v2Pools: [], v3Pools: [], gauges: /* @__PURE__ */ new Map() };
  }
}
export async function fetchV3PoolTicks(poolAddress, chainId = UniverseChainId.Mainnet, skip = 0) {
  try {
    const subgraphs = getBscSubgraphs(chainId);
    const v3Subgraph = subgraphs.v3;
    const data = await fetchFromSubgraph(v3Subgraph, {
      query: V3_POOL_TICKS_QUERY,
      variables: {
        poolAddress: poolAddress.toLowerCase(),
        skip
      },
      requestId: `fetchV3PoolTicks:${poolAddress}:${skip}`
    });
    return data?.ticks ?? [];
  } catch (error) {
    console.error("Failed to fetch V3 pool ticks:", error);
    return [];
  }
}
export async function fetchAllV3PoolTicks(poolAddress, chainId = UniverseChainId.Mainnet) {
  const allTicks = [];
  let skip = 0;
  const batchSize = 1e3;
  try {
    while (true) {
      const ticks = await fetchV3PoolTicks(poolAddress, chainId, skip);
      if (ticks.length === 0) {
        break;
      }
      allTicks.push(...ticks);
      if (ticks.length < batchSize) {
        break;
      }
      skip += batchSize;
    }
    return allTicks;
  } catch (error) {
    console.error("Failed to fetch all V3 pool ticks:", error);
    return allTicks;
  }
}
export async function fetchPoolPriceHistory(poolAddress, chainId = UniverseChainId.Mainnet, from = 0, pageSize = 1e3, skip = 0) {
  try {
    const subgraphs = getBscSubgraphs(chainId);
    const v3Subgraph = subgraphs.v3;
    const data = await fetchFromSubgraph(v3Subgraph, {
      query: V3_POOL_PRICE_HISTORY_QUERY,
      variables: {
        poolId: poolAddress.toLowerCase(),
        from,
        pageSize,
        skip
      },
      requestId: `fetchPoolPriceHistory:${poolAddress}:${skip}`
    });
    return data?.poolDayDatas ?? [];
  } catch (error) {
    console.error("Failed to fetch V3 pool price history:", error);
    return [];
  }
}
export async function fetchAllPoolPriceHistory(poolAddress, chainId = UniverseChainId.Mainnet, from = 0) {
  const allEntries = [];
  const pageSize = 1e3;
  let skip = 0;
  try {
    while (true) {
      const entries = await fetchPoolPriceHistory(poolAddress, chainId, from, pageSize, skip);
      if (!entries.length) {
        break;
      }
      allEntries.push(...entries);
      if (entries.length < pageSize) {
        break;
      }
      skip += pageSize;
    }
    return allEntries;
  } catch (error) {
    console.error("Failed to fetch all pool price history:", error);
    return allEntries;
  }
}
export async function fetchPoolPriceHistoryHourly(poolAddress, chainId = UniverseChainId.Mainnet, from = 0, pageSize = 1e3, skip = 0) {
  try {
    const subgraphs = getBscSubgraphs(chainId);
    const v3Subgraph = subgraphs.v3;
    const data = await fetchFromSubgraph(v3Subgraph, {
      query: V3_POOL_HOUR_HISTORY_QUERY,
      variables: {
        poolId: poolAddress.toLowerCase(),
        from,
        pageSize,
        skip
      },
      requestId: `fetchPoolPriceHistoryHourly:${poolAddress}:${skip}`
    });
    return (data?.poolHourDatas ?? []).map((row) => ({
      date: String(row.periodStartUnix),
      token0Price: row.token0Price,
      token1Price: row.token1Price,
      open: row.open,
      high: row.high,
      low: row.low,
      close: row.close
    }));
  } catch (error) {
    console.error("Failed to fetch V3 pool hourly price history:", error);
    return [];
  }
}
export async function fetchAllPoolPriceHistoryHourly(poolAddress, chainId = UniverseChainId.Mainnet, from = 0) {
  const allEntries = [];
  const pageSize = 1e3;
  let skip = 0;
  try {
    while (true) {
      const entries = await fetchPoolPriceHistoryHourly(poolAddress, chainId, from, pageSize, skip);
      if (!entries.length) {
        break;
      }
      allEntries.push(...entries);
      if (entries.length < pageSize) {
        break;
      }
      skip += pageSize;
    }
    return allEntries;
  } catch (error) {
    console.error("Failed to fetch all pool hourly price history:", error);
    return allEntries;
  }
}
async function fetchAllPaged(fetchPage, pageSize = 1e3) {
  const all = [];
  let skip = 0;
  try {
    while (true) {
      const page = await fetchPage(skip, pageSize);
      if (!page.length) {
        break;
      }
      all.push(...page);
      if (page.length < pageSize) {
        break;
      }
      skip += pageSize;
    }
  } catch (error) {
    console.error("[BscSubgraph] paginated fetch failed:", error);
  }
  return all;
}
const V3_POOL_VOLUME_DAY_QUERY = `
  query PoolVolumeDay($poolId: String!, $from: Int!, $pageSize: Int!, $skip: Int!) {
    poolDayDatas(
      first: $pageSize
      skip: $skip
      where: { pool: $poolId, date_gte: $from }
      orderBy: date
      orderDirection: asc
    ) {
      date
      volumeUSD
    }
  }
`;
const V3_POOL_VOLUME_HOUR_QUERY = `
  query PoolVolumeHour($poolId: String!, $from: Int!, $pageSize: Int!, $skip: Int!) {
    poolHourDatas(
      first: $pageSize
      skip: $skip
      where: { pool: $poolId, periodStartUnix_gte: $from }
      orderBy: periodStartUnix
      orderDirection: asc
    ) {
      periodStartUnix
      volumeUSD
    }
  }
`;
export async function fetchAllV3PoolVolumeHistory(poolAddress, chainId = UniverseChainId.Mainnet, from = 0, hourly = false) {
  const poolId = poolAddress.toLowerCase();
  return fetchAllPaged(async (skip, pageSize) => {
    const v3Subgraph = getBscSubgraphs(chainId).v3;
    if (hourly) {
      const data2 = await fetchFromSubgraph(
        v3Subgraph,
        {
          query: V3_POOL_VOLUME_HOUR_QUERY,
          variables: { poolId, from, pageSize, skip },
          requestId: `fetchV3PoolVolumeHourly:${poolId}:${skip}`
        }
      );
      return (data2?.poolHourDatas ?? []).map((row) => ({ time: Number(row.periodStartUnix), volumeUSD: Number(row.volumeUSD) }));
    }
    const data = await fetchFromSubgraph(v3Subgraph, {
      query: V3_POOL_VOLUME_DAY_QUERY,
      variables: { poolId, from, pageSize, skip },
      requestId: `fetchV3PoolVolumeDaily:${poolId}:${skip}`
    });
    return (data?.poolDayDatas ?? []).map((row) => ({ time: Number(row.date), volumeUSD: Number(row.volumeUSD) }));
  });
}
const V2_PAIR_DAY_QUERY = `
  query V2PairDay($pairId: String!, $from: Int!, $pageSize: Int!, $skip: Int!) {
    pairDayDatas(
      first: $pageSize
      skip: $skip
      where: { pairAddress: $pairId, date_gte: $from }
      orderBy: date
      orderDirection: asc
    ) {
      date
      reserve0
      reserve1
      dailyVolumeUSD
    }
  }
`;
const V2_PAIR_HOUR_QUERY = `
  query V2PairHour($pairId: String!, $from: Int!, $pageSize: Int!, $skip: Int!) {
    pairHourDatas(
      first: $pageSize
      skip: $skip
      where: { pair: $pairId, hourStartUnix_gte: $from }
      orderBy: hourStartUnix
      orderDirection: asc
    ) {
      hourStartUnix
      reserve0
      reserve1
      hourlyVolumeUSD
    }
  }
`;
export async function fetchAllV2PairHistory(pairAddress, chainId = UniverseChainId.Mainnet, from = 0, hourly = false) {
  const pairId = pairAddress.toLowerCase();
  return fetchAllPaged(async (skip, pageSize) => {
    const v2Subgraph = getBscSubgraphs(chainId).v2;
    if (hourly) {
      const data2 = await fetchFromSubgraph(v2Subgraph, {
        query: V2_PAIR_HOUR_QUERY,
        variables: { pairId, from, pageSize, skip },
        requestId: `fetchV2PairHourly:${pairId}:${skip}`
      });
      return (data2?.pairHourDatas ?? []).map((row) => ({
        time: Number(row.hourStartUnix),
        reserve0: Number(row.reserve0),
        reserve1: Number(row.reserve1),
        volumeUSD: Number(row.hourlyVolumeUSD)
      }));
    }
    const data = await fetchFromSubgraph(v2Subgraph, {
      query: V2_PAIR_DAY_QUERY,
      variables: { pairId, from, pageSize, skip },
      requestId: `fetchV2PairDaily:${pairId}:${skip}`
    });
    return (data?.pairDayDatas ?? []).map((row) => ({
      time: Number(row.date),
      reserve0: Number(row.reserve0),
      reserve1: Number(row.reserve1),
      volumeUSD: Number(row.dailyVolumeUSD)
    }));
  });
}
const TOKEN_PRICE_DAY_QUERY = `
  query TokenPriceDay($tokenId: String!, $from: Int!, $pageSize: Int!, $skip: Int!) {
    tokenDayDatas(
      first: $pageSize
      skip: $skip
      where: { token: $tokenId, date_gte: $from }
      orderBy: date
      orderDirection: asc
    ) {
      date
      priceUSD
      open
      high
      low
      close
    }
  }
`;
const TOKEN_PRICE_HOUR_QUERY = `
  query TokenPriceHour($tokenId: String!, $from: Int!, $pageSize: Int!, $skip: Int!) {
    tokenHourDatas(
      first: $pageSize
      skip: $skip
      where: { token: $tokenId, periodStartUnix_gte: $from }
      orderBy: periodStartUnix
      orderDirection: asc
    ) {
      periodStartUnix
      priceUSD
      open
      high
      low
      close
    }
  }
`;
function toTokenPricePoint(row) {
  const time = Number(row.date ?? row.periodStartUnix);
  const price = Number(row.priceUSD);
  const close = row.close !== void 0 ? Number(row.close) : price;
  return {
    time,
    priceUSD: price,
    open: row.open !== void 0 ? Number(row.open) : close,
    high: row.high !== void 0 ? Number(row.high) : close,
    low: row.low !== void 0 ? Number(row.low) : close,
    close
  };
}
export async function fetchAllTokenPriceHistory(tokenAddress, chainId = UniverseChainId.Mainnet, from = 0, hourly = false) {
  const tokenId = tokenAddress.toLowerCase();
  return fetchAllPaged(async (skip, pageSize) => {
    const v3Subgraph = getBscSubgraphs(chainId).v3;
    const query = hourly ? TOKEN_PRICE_HOUR_QUERY : TOKEN_PRICE_DAY_QUERY;
    const key = hourly ? "tokenHourDatas" : "tokenDayDatas";
    const data = await fetchFromSubgraph(v3Subgraph, {
      query,
      variables: { tokenId, from, pageSize, skip },
      requestId: `fetchTokenPrice:${hourly ? "hourly" : "daily"}:${tokenId}:${skip}`
    });
    return (data?.[key] ?? []).map(toTokenPricePoint);
  });
}
